import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, Badge, Button, Input, Label,
  Modal, ModalHeader, ModalBody, ModalFooter, Form, Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "components/Common/Breadcrumb";
import {
  getContactsData, searchContactsData, getContactSummary, getContactById, createContact,
} from "../../../helpers/contact_list_helper";
import { updateContact, deleteContact, updateContactExpiry } from "../../../helpers/api_helper";

const CONTACT_TYPES = ["CLIENT", "LEAD", "VENDOR", "PARTNER", "EMPLOYEE", "OTHER"];
const EMPTY_FORM = { firstName: "", lastName: "", mobile: "", email: "", contactType: "CLIENT" };

const ContactsAll = () => {
  document.title = "Contacts | All Contacts";

  const [pagination, setPagination] = useState({ pageSize: 10, page: 0 });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const [keyword, setKeyword] = useState("");
  const [activeKeyword, setActiveKeyword] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const [refreshTick, setRefreshTick] = useState(0);
  const reload = () => setRefreshTick((t) => t + 1);

  const loadContacts = () => {
    setLoading(true);
    const fetcher = activeKeyword
      ? searchContactsData({ keyword: activeKeyword, page: pagination.page, pageSize: pagination.pageSize })
      : getContactsData({ page: pagination.page, pageSize: pagination.pageSize });
    fetcher
      .then((r) => { setRows(r.rows); setTotal(r.total); setTotalPages(r.totalPages); })
      .catch(() => { setRows([]); setTotal(0); setTotalPages(0); })
      .finally(() => setLoading(false));
  };

  const loadSummary = () => getContactSummary().then(setSummary).catch(() => setSummary(null));

  useEffect(loadContacts, [pagination, activeKeyword, refreshTick]);
  useEffect(() => { loadSummary(); }, []);

  const onChangePagination = ({ page, limit }) =>
    setPagination((p) => ({ ...p, page: page ?? p.page, pageSize: limit ?? p.pageSize }));

  // Debounced server-side search — one box, searches all contacts as you type.
  useEffect(() => {
    const h = setTimeout(() => {
      setPagination((p) => ({ ...p, page: 0 }));
      setActiveKeyword(keyword.trim());
    }, 400);
    return () => clearTimeout(h);
  }, [keyword]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({});
    try { setDetail(await getContactById(id)); }
    catch { setDetail(null); Swal.fire("Error", "Could not load contact.", "error"); }
    finally { setDetailLoading(false); }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.mobile.trim()) return;
    try {
      setSaving(true);
      await createContact(form);
      setAddOpen(false); setForm(EMPTY_FORM);
      Swal.fire("Saved", "Contact added.", "success");
      reload(); loadSummary();
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || "Could not add contact.", "error");
    } finally { setSaving(false); }
  };

  const openEdit = (c) => {
    setEditForm({
      id: c.id,
      firstName: c.firstName || "",
      lastName: c.lastName || "",
      mobile: c.mobile || "",
      email: c.email || "",
      contactType: c.contactType || "CLIENT",
      expiryDate: c.expiryDate ? moment(c.expiryDate).format("YYYY-MM-DD") : "",
      _origExpiry: c.expiryDate ? moment(c.expiryDate).format("YYYY-MM-DD") : "",
    });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm.firstName.trim() || !editForm.mobile.trim()) return;
    try {
      setEditSaving(true);
      await updateContact(editForm.id, editForm);
      // Expiry has its own endpoint (honours null to clear); only call it on change.
      if (editForm.expiryDate !== editForm._origExpiry) {
        await updateContactExpiry(editForm.id, editForm.expiryDate || null);
      }
      setEditOpen(false);
      Swal.fire("Saved", "Contact updated.", "success");
      reload();
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || "Could not update contact.", "error");
    } finally { setEditSaving(false); }
  };

  const doDelete = async (c) => {
    const ok = await Swal.fire({
      title: "Delete contact?",
      text: `${c.name} will be removed from all lists.`,
      icon: "warning", showCancelButton: true, confirmButtonColor: "#d33", confirmButtonText: "Delete",
    });
    if (!ok.isConfirmed) return;
    try {
      await deleteContact(c.id);
      Swal.fire("Deleted", "Contact deleted.", "success");
      reload(); loadSummary();
    } catch { Swal.fire("Error", "Could not delete contact.", "error"); }
  };

  const stats = [
    { title: "Total Contacts", icon: "bx-group", value: summary?.totalContacts },
    { title: "Active", icon: "bx-user-check", value: summary?.activeContacts },
    { title: "Favorites", icon: "bx-star", value: summary?.favoriteContacts },
  ];

  const columns = useMemo(() => [
    { header: "Name", accessorKey: "name", enableColumnFilter: false },
    { header: "Mobile", accessorKey: "mobile", enableColumnFilter: false },
    {
      header: "Type", accessorKey: "contactType", enableColumnFilter: false,
      cell: (info) => info.getValue() ? <Badge color="light" className="text-dark">{info.getValue()}</Badge> : "—",
    },
    { header: "County", accessorKey: "county", enableColumnFilter: false, cell: (info) => info.getValue() || "—" },
    { header: "Department", accessorKey: "department", enableColumnFilter: false, cell: (info) => info.getValue() || "Universal" },
    {
      header: "Actions",
      enableColumnFilter: false,
      cell: (cell) => {
        const row = cell.row.original;
        return (
          <div className="d-flex gap-3">
            <Link to="#" className="text-primary" onClick={(e) => { e.preventDefault(); openDetail(row.id); }}>
              <i className="mdi mdi-eye font-size-18" />
            </Link>
            <Link to="#" className="text-success" onClick={(e) => { e.preventDefault(); openEdit(row); }}>
              <i className="mdi mdi-pencil font-size-18" />
            </Link>
            <Link to="#" className="text-danger" onClick={(e) => { e.preventDefault(); doDelete(row); }}>
              <i className="mdi mdi-delete font-size-18" />
            </Link>
          </div>
        );
      },
    },
  ], []);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Contacts" breadcrumbItem="All Contacts" />
        <Row>
          {stats.map((s, i) => (
            <Col md="4" key={i}>
              <Card className="mini-stats-wid">
                <CardBody>
                  <div className="d-flex">
                    <div className="flex-grow-1">
                      <p className="text-muted fw-medium mb-2">{s.title}</p>
                      <h4 className="mb-0">{(s.value || 0).toLocaleString()}</h4>
                    </div>
                    <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                      <span className="avatar-title rounded-circle bg-primary">
                        <i className={"bx " + s.icon + " font-size-24"}></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">Contacts</h4>

                <Row className="mb-3 align-items-center">
                  <Col md={8}>
                    <div className="d-flex align-items-center">
                      <Input
                        style={{ maxWidth: 370 }}
                        placeholder="Search all contacts by name, email, mobile…"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                      />
                      {activeKeyword && (
                        <small className="text-muted ms-3">{total.toLocaleString()} result(s) for “{activeKeyword}”</small>
                      )}
                    </div>
                  </Col>
                  <Col md={4} className="text-md-end mt-2 mt-md-0">
                    <Button color="primary" onClick={() => setAddOpen(true)}>
                      <i className="mdi mdi-plus"></i> Add Contact
                    </Button>
                  </Col>
                </Row>

                <TableContainer
                  columns={columns}
                  data={rows}
                  isGlobalFilter={false}
                  isPagination={true}
                  isCustomPageSize={true}
                  pageSizeAtBottom={true}
                  paginationData={pagination}
                  onChangePagination={onChangePagination}
                  manualPagination={true}
                  pageCount={totalPages}
                  totalRows={total}
                  tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                  theadClass="table-light"
                  paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  pagination="pagination"
                />
                {loading && <div className="text-center text-muted py-2"><Spinner size="sm" /> Loading…</div>}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Contact */}
      <Modal isOpen={addOpen} toggle={() => setAddOpen(!addOpen)} centered>
        <ModalHeader toggle={() => setAddOpen(!addOpen)}>Add Contact</ModalHeader>
        <Form onSubmit={submitAdd}>
          <ModalBody>
            <Row>
              <Col md={6} className="mb-3">
                <Label>First name *</Label>
                <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required />
              </Col>
              <Col md={6} className="mb-3">
                <Label>Last name</Label>
                <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
              </Col>
              <Col md={6} className="mb-3">
                <Label>Mobile *</Label>
                <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="0712 345 678" required />
              </Col>
              <Col md={6} className="mb-3">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Col>
              <Col md={6} className="mb-3">
                <Label>Type</Label>
                <Input type="select" value={form.contactType} onChange={(e) => setForm({ ...form, contactType: e.target.value })}>
                  {CONTACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </Input>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button color="primary" type="submit" disabled={saving || !form.firstName.trim() || !form.mobile.trim()}>
              {saving ? <Spinner size="sm" /> : "Save contact"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Edit Contact */}
      <Modal isOpen={editOpen} toggle={() => setEditOpen(!editOpen)} centered>
        <ModalHeader toggle={() => setEditOpen(!editOpen)}>Edit Contact</ModalHeader>
        {editForm && (
          <Form onSubmit={submitEdit}>
            <ModalBody>
              <Row>
                <Col md={6} className="mb-3">
                  <Label>First name *</Label>
                  <Input value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} required />
                </Col>
                <Col md={6} className="mb-3">
                  <Label>Last name</Label>
                  <Input value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                  <Label>Mobile *</Label>
                  <Input value={editForm.mobile} onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })} required />
                </Col>
                <Col md={6} className="mb-3">
                  <Label>Email</Label>
                  <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                  <Label>Type</Label>
                  <Input type="select" value={editForm.contactType} onChange={(e) => setEditForm({ ...editForm, contactType: e.target.value })}>
                    {CONTACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </Input>
                </Col>
                <Col md={6} className="mb-3">
                  <Label>Expiry date</Label>
                  <Input type="date" value={editForm.expiryDate} onChange={(e) => setEditForm({ ...editForm, expiryDate: e.target.value })} />
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" outline onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button color="primary" type="submit" disabled={editSaving || !editForm.firstName.trim() || !editForm.mobile.trim()}>
                {editSaving ? <Spinner size="sm" /> : "Save changes"}
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Modal>

      {/* Contact detail */}
      <Modal isOpen={!!detail} toggle={() => setDetail(null)} centered>
        <ModalHeader toggle={() => setDetail(null)}>Contact Detail</ModalHeader>
        <ModalBody>
          {detailLoading ? (
            <div className="text-center py-3"><Spinner /></div>
          ) : detail ? (
            <Row>
              {[
                ["Name", detail.name],
                ["Mobile", detail.mobile],
                ["Email", detail.email],
                ["Type", detail.contactType],
                ["County", detail.county],
                ["Constituency", detail.constituency],
                ["Ward", detail.ward],
                ["Department", detail.department || "Universal"],
                ["Country", detail.countryIso2],
                ["Gender", detail.gender],
                ["Identity No", detail.identityNo],
                ["Birthday", detail.birthday ? moment(detail.birthday).format("DD MMM YYYY") : ""],
                ["Expiry", detail.expiryDate ? moment(detail.expiryDate).format("DD MMM YYYY") : ""],
                ["Added", detail.createdOn ? moment(detail.createdOn).format("DD MMM YYYY") : ""],
              ].map(([label, value]) => (
                <Col md={6} className="mb-3" key={label}>
                  <p className="text-muted text-uppercase font-size-11 mb-1">{label}</p>
                  <div className="fw-medium">{value || "—"}</div>
                </Col>
              ))}
            </Row>
          ) : null}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ContactsAll;
